import { Component, computed, inject, Input, OnChanges, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BudgetFormDirective } from "./budget-form.directive";
import { HelperNumericInput } from "../../../shared/utils/helper-numeric-input";
import { BudgetIntervalType } from "@bitwarden/web-vault/app/models/types/budget.types";
import { CurrencyType } from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { BudgetStoreModel } from "@bitwarden/web-vault/app/models/store/budget.store.model";
import { BudgetStoreService } from "@bitwarden/web-vault/app/services/store/budget/budget.store.service";
import { BudgetView } from "@bitwarden/web-vault/app/models/view/budget/budget.view";
import { BudgetUtils } from "@bitwarden/web-vault/app/models/view/budget/budget.utils";
import { CategoryView } from "@bitwarden/web-vault/app/models/view/category/category.view";
import { MatDialog } from "@angular/material/dialog";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { CategoriesAddEditComponent } from "../../manage/manage-categories/categories-add-edit/categories-add-edit.component";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import {
  DEFAULT_CATEGORY_ID,
  DEFAULT_CATEGORY_NAME,
} from "../../../models/constants/category.constants";
import { RightPanelService } from "@bitwarden/web-vault/app/components/dialog/right-panel.service";

@Component({
  selector: "app-add-budget-form",
  templateUrl: "./add-budget-form.component.html",
})
export class AddBudgetFormComponent extends BudgetFormDirective implements OnInit {
  private readonly rightPanel = inject(RightPanelService);
  @Input() closeDialog!: CallableFunction;
  @Input() budget?: BudgetView;
  @Input() onBudgetSaved?: (budget: BudgetView) => void;

  formBuilder: FormBuilder = inject(FormBuilder);
  formGroup: FormGroup;
  isEditMode: boolean = false;
  /** True while a budget is being written; the panel won't close on click-away/Escape meanwhile. */
  protected submitting = false;

  /** Smallest allowed budget amount; drives both the form validator and the inline min error. */
  readonly minAmount = 0.01;

  // Daily/yearly are intentionally omitted until those cadences ship in the UI.
  intervalTypes: BudgetIntervalType[] = ["weekly", "monthly"];
  intervalTypesLabels: string[] = this.intervalTypes.map(
    (type) => type.charAt(0).toUpperCase() + type.slice(1),
  );
  private userStoreService: UserStoreService = inject(UserStoreService);
  private budgetStoreService: BudgetStoreService = inject(BudgetStoreService);
  private dialog: MatDialog = inject(MatDialog);
  private confirmService: ConfirmationDialogService = inject(ConfirmationDialogService);

  protected defaultSystemCurrency: CurrencyType =
    this.userStoreService.preferences.preferenceView().coreCurrency;
  constructor() {
    super();
  }

  ngOnInit(): void {
    if (this.budget) {
      this.initializeEdit();
      return;
    }

    this.initializeAdd();
  }

  initializeAdd() {
    this.isEditMode = false;
    this.formGroup = this.formBuilder.group({
      name: [null, [Validators.required, Validators.pattern(/\S/)]],
      category: [null, [Validators.required]],
      amount: [
        null,
        [HelperNumericInput.isNumberValidator, Validators.required, Validators.min(this.minAmount)],
      ],
      period: ["monthly", Validators.required],
      currency: [this.defaultSystemCurrency, Validators.required],
      startDate: [this.datePipe.transform(Date.now(), "yyyy-MM-dd"), Validators.required],
    });
  }

  initializeEdit() {
    this.isEditMode = true;

    this.formGroup = this.formBuilder.group({
      name: [this.budget?.name || null, [Validators.required, Validators.pattern(/\S/)]],
      category: [
        this.budget?.categoriesLink?.length ? this.budget.categoriesLink[0].id : null,
        [Validators.required],
      ],
      amount: [
        this.budget?.amount ?? null,
        [HelperNumericInput.isNumberValidator, Validators.required, Validators.min(this.minAmount)],
      ],
      period: [this.budget?.interval || "monthly", Validators.required],
      currency: [this.budget?.currency || this.defaultSystemCurrency, Validators.required],
      startDate: [
        this.budget?.startDate
          ? this.datePipe.transform(this.budget.startDate, "yyyy-MM-dd")
          : this.datePipe.transform(Date.now(), "yyyy-MM-dd"),
        Validators.required,
      ],
    });

    // Set selected categories if budget exists
    if (this.budget?.categoriesLink?.length) {
      const cats = this.budget.categoriesLink.reduce((acc, link) => {
        const cat = this.userStoreService.categories
          .categoriesWithoutDefault()
          .find((cat: CategoryView) => cat.id === link.id);
        if (cat) {
          acc.push(cat);
        }
        return acc;
      }, []);
      this.selectedCategories.set(cats);
    }
  }

  categoryNames = computed(() => {
    const categories = this.userStoreService.categories.categoriesWithoutDefault();
    const selectedCategories = this.selectedCategories();
    const availableCategories = categories.filter(
      (cat) => cat.id !== DEFAULT_CATEGORY_ID && cat.name !== DEFAULT_CATEGORY_NAME,
    );

    if (selectedCategories.length > 0) {
      return availableCategories
        .filter((cat) => !selectedCategories.some((selCat) => selCat.id === cat.id))
        .map((cat) => cat.name);
    }
    return availableCategories.map((cat) => cat.name);
  });

  selectedCategories = signal<CategoryView[]>([]);

  setCategory(categoryName: string) {
    const category = this.userStoreService.categories
      .categoryViews()
      .find((cat) => cat.name === categoryName);
    if (category) {
      this.selectedCategories.set([...this.selectedCategories(), category]);
    }
    this.formGroup.controls["category"].setValue(categoryName);
  }

  addManualCategory() {
    this.rightPanel.open(DialogWrapperComponent, {
      data: {
        title: "createNewCategory",
        showBackButton: false,
        component: CategoriesAddEditComponent,
        fullyCustomised: true,
        showHeaderCloseButton: true,
        componentInputs: {
          category: null,
          actionSucceeded: this.addCategorySuccessful.bind(this),
        },
      },
    });
  }

  addCategorySuccessful(response: { newCategoryText: string; successfulText: string }) {
    const newCategory = this.userStoreService.categories
      .categoryViews()
      .find((cat) => cat.name === response.newCategoryText);
    if (newCategory) {
      this.selectedCategories.set([...this.selectedCategories(), newCategory]);
      this.formGroup.controls["category"].setValue(response.newCategoryText);
    }

    this.confirmService.notify(NotificationEnum.success, "succeeded", response.successfulText);
  }

  removeSelectedCategory(category: CategoryView) {
    this.selectedCategories.set(this.selectedCategories().filter((cat) => cat.id !== category.id));
    if (this.selectedCategories().length === 0) {
      this.formGroup.controls["category"].setValue(null);
    }
  }

  onInput(value: string, controlName: string) {
    this.formGroup.controls[controlName]?.setValue(value);
  }

  onDateChange(date: string, controlName: string) {
    this.formGroup.controls[controlName]?.setValue(date);
    this.formGroup.controls[controlName]?.markAsTouched();
  }

  setPeriod(periodLabel: string) {
    const period = periodLabel.toLowerCase() as BudgetIntervalType;
    this.formGroup.controls["period"]?.setValue(period);
    this.formGroup.controls["period"]?.markAsTouched();
  }

  selectedPeriodLabel(): string {
    const period = this.formGroup?.controls["period"]?.value ?? "monthly";
    return period.charAt(0).toUpperCase() + period.slice(1);
  }

  async submit() {
    this.formGroup.markAllAsTouched();

    if (!this.formGroup.valid) {
      return;
    }

    if (this.isNameDuplicate(this.formGroup.controls["name"]?.value)) {
      this.confirmService.notify(NotificationEnum.warning, "warning", "duplicateNameError");
      return;
    }

    if (this.isEditMode && this.budget) {
      // Update existing budget
      this.updateBudget();
      return;
    }

    this.addNewBudget();
  }

  private isNameDuplicate(name: string): boolean {
    return BudgetUtils.isNameDuplicate(
      name,
      this.budgetStoreService.budget.budgetViews() ?? [],
      this.budget?.id,
    );
  }

  private async addNewBudget() {
    const newBudget: BudgetStoreModel = {
      v: 0,
      nm: this.formGroup.controls["name"]?.value,
      am: parseFloat(this.formGroup.controls["amount"]?.value),
      cur: this.formGroup.controls["currency"]?.value || this.defaultSystemCurrency,
      int: this.formGroup.controls["period"]?.value as BudgetIntervalType,
      sdt: null,
      edt: null,
      spd: "continuous" as const,
      stp: 1,
      ctId: this.selectedCategories().map((cat) => cat.id),
      id: crypto.randomUUID(),
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      vid: null,
    };

    const budgetView = new BudgetView(newBudget);
    this.submitting = true;
    try {
      await this.budgetStoreService.budget.save(budgetView);
    } finally {
      this.submitting = false;
    }
    this.onBudgetSaved?.(budgetView);

    this.confirmService.notify(NotificationEnum.success, "succeeded", "saved");
    if (typeof this.closeDialog === "function") {
      this.closeDialog();
      /*  call the getSource on table so it affects all the rest  */
    }
  }

  private async updateBudget() {
    const budgetClone = this.budget.clone();
    budgetClone.name = this.formGroup.controls["name"]?.value;
    // Amount + picker are both in the budget's own currency, so persist them directly.
    budgetClone.amount = parseFloat(this.formGroup.controls["amount"]?.value);
    budgetClone.currency = this.formGroup.controls["currency"]?.value || this.defaultSystemCurrency;
    budgetClone.interval = this.formGroup.controls["period"]?.value as BudgetIntervalType;
    budgetClone.startDate = new Date(this.formGroup.controls["startDate"]?.value);
    budgetClone.categoriesLink = this.selectedCategories().map((cat) => ({ id: cat.id }));

    this.submitting = true;
    try {
      await this.budgetStoreService.budget.save(budgetClone);
    } finally {
      this.submitting = false;
    }

    this.confirmService.notify(NotificationEnum.success, "succeeded", "updated");
    if (typeof this.closeDialog === "function") {
      this.closeDialog();
    }
  }
}
